import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { ID } from '@state/base';
import { environment } from '@env/environment';
import { FundMessagesRepository } from './fund-messages.repository';
import { FundMessage, MessageRecipientGroup, ShareDocumentsParams } from './fund-message.model';
import { InvestorBulkEmailRequest } from '$interfaces/investor_bulk_email/request';
import { InvestorBulkEmailResponse } from '$interfaces/investor_bulk_email/response';
import { rpc } from '$interfaces/flow-rpc.types';

@Injectable({ providedIn: 'root' })
export class FundMessagesService {
  private http = inject(HttpClient);
  private fundMessagesRepository = inject(FundMessagesRepository);

  baseUrl = `${environment.apiUrl}/v1/messages`;

  // Messages a set of a vehicle's investors. The backend records the
  // EmailNotification synchronously, then queues one job per recipient and
  // responds before anything is delivered — a success here means "accepted",
  // and delivery status shows up per recipient on the fund's Messages tab.
  send$(params: InvestorBulkEmailRequest): Observable<InvestorBulkEmailResponse> {
    return this.http.post<InvestorBulkEmailResponse>(`${environment.apiUrl}/rpc/${rpc.investorBulkEmail}`, params);
  }

  // Shares the given documents (View & Download) with all of a vehicle's
  // investors and sends a single message linking into the fund's documents in
  // FLI (a direct document link for a single share, the documents index for
  // multiple). Uses communication preferences only, so the legacy notify_team is omitted.
  shareDocumentsWithInvestors$(params: ShareDocumentsParams): Observable<object> {
    let url = `${environment.apiUrl}/v1/vehicles/${params.vehicle_id}/documents/share_with_investors`;
    return this.http.post(url, params);
  }

  get(vehicle_id: string | ID): void {
    this.get$(vehicle_id).subscribe({
      next: (messages: FundMessage[]) => {
        this.fundMessagesRepository.setFundMessages(messages);
      }
    });
  }

  get$(vehicle_id: ID, providerId?: ID): Observable<FundMessage[]> {
    let options = {};
    if (providerId) {
      let headers = new HttpHeaders({ 'Provider-Id': providerId as string });
      options = { headers };
    }

    let url = `${this.baseUrl}?vehicle_id=${vehicle_id}`;
    return this.http.get(url, options) as Observable<FundMessage[]>;
  }

  getAndStoreFundMessages$(vehicle_id: ID, providerId?: ID): Observable<FundMessage[]> {
    return this.get$(vehicle_id, providerId).pipe(
      tap({
        next: (messages: FundMessage[]) => {
          this.fundMessagesRepository.setFundMessages(messages);
        }
      })
    );
  }

  getFundMessagePersonalizations$(
    vehicle_id: ID,
    email_notification_id: ID,
    providerId?: ID
  ): Observable<MessageRecipientGroup[]> {
    let options: { headers?: HttpHeaders; params: { email_notification_id: ID; vehicle_id: ID } } = {
      params: {
        email_notification_id,
        vehicle_id
      }
    };

    if (providerId) {
      let headers = new HttpHeaders({ 'Provider-Id': providerId as string });
      options.headers = headers;
    }

    let url = `${environment.apiUrl}/v1/messages/personalizations`;
    return this.http.get<MessageRecipientGroup[]>(url, options);
  }

  getAndUpdateFundMessagePersonalizations$(
    vehicle_id: ID,
    email_notification_id: ID,
    providerId?: ID
  ): Observable<MessageRecipientGroup[]> {
    return this.getFundMessagePersonalizations$(vehicle_id, email_notification_id, providerId).pipe(
      tap({
        next: (personalizations) => {
          this.fundMessagesRepository.updateFundMessage(
            email_notification_id, { recipient_groups: personalizations }
          );
        }
      })
    );
  }
}
